/**
 * GameCanvas — the React ↔ engine bridge.
 *
 * React owns NONE of the per-frame simulation (that would thrash the vDOM).
 * Instead this component:
 *   1. boots the engine once (RenderRoot + GameState + GameLoop + input + audio)
 *   2. mounts the WebGL canvases into a plain <div> via a ref
 *   3. polls a cheap `snapshot()` a few times a second to feed the HUD
 *
 * All game mutation happens through refs, so re-renders are limited to the HUD.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { RenderRoot } from "@/render/RenderRoot";
import { GameState, type HudSnapshot } from "@/engine/GameState";
import { GameLoop } from "@/engine/GameLoop";
import { PointerControls } from "@/input/PointerControls";
import { AudioBus } from "@/audio/AudioBus";
import HUD from "./HUD";
import Onboarding from "./Onboarding";
import MainMenu from "./MainMenu";
import HubCanvas from "@/hub/HubCanvas";
import { loadCloudSave, saveCloudSave, deleteCloudSave } from "@/net/cloudSave";
import { joinOnlinePresence } from "@/net/presence";
import { startSession, heartbeatSession, endSession } from "@/net/sessions";
import { tierFromUpgrades } from "@/net/hub";
import type { Account } from "@/net/auth";

interface Props {
  account: Account;
  onLogout: () => void;
}

export default function GameCanvas({ account, onLogout }: Props) {
  const mountRef = useRef<HTMLDivElement>(null);
  const stateRef = useRef<GameState | null>(null);
  const audioRef = useRef<AudioBus | null>(null);
  const resettingRef = useRef(false); // true → skip the unmount save (hard reset)
  const [snap, setSnap] = useState<HudSnapshot | null>(null);
  const [onboard, setOnboard] = useState(false);
  const [started, setStarted] = useState(false); // false → the main menu is showing
  const [inHub, setInHub] = useState(false); // true → the shared multiplayer hub is showing
  const [online, setOnline] = useState(0); // live "players online" count (presence)
  const [hubTier, setHubTier] = useState(0); // hull tier from the cloud save → gates the Hub (Mk II+)
  const [aiming, setAiming] = useState(false);
  const aimingRef = useRef(false); // next world tap locks the cannon aim
  const tipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!started) return; // hold on the main menu until the player presses PLAY
    const mount = mountRef.current;
    if (!mount) return;

    let cancelled = false;
    let ready = false;
    const render = new RenderRoot();
    const state = new GameState();
    state.accountName = account.username; // account-based admin + nameplate context
    const audio = new AudioBus();
    stateRef.current = state;
    audioRef.current = audio;

    // This account's cloud save is loaded asynchronously in mount().then() below (before the
    // loop starts). Autosave to the cloud (+ a local offline cache) on a relaxed cadence — much
    // gentler on the shared database than the old every-2.5s local write.
    const save = (): void => {
      if (resettingRef.current || !stateRef.current) return; // don't re-write during a reset
      void saveCloudSave(account.id, stateRef.current.serialize());
    };
    const saveTimer = window.setInterval(save, 30000);

    // --- asteroid hover tooltip (imperative DOM → no React churn per move) ---
    const hover: { id: string | null } = { id: null };
    const showTip = (cx: number, cy: number, ore: number, oreMax: number): void => {
      const el = tipRef.current;
      if (!el) return;
      el.textContent = `${fmt(ore)} / ${fmt(oreMax)} minerals`;
      el.style.left = `${cx}px`;
      el.style.top = `${cy}px`;
      el.style.display = "block";
    };
    const hideTip = (): void => {
      hover.id = null;
      if (tipRef.current) tipRef.current.style.display = "none";
    };
    const refreshTip = (): void => {
      if (!hover.id) return;
      const info = state.bodyOre(hover.id);
      if (info && tipRef.current) {
        tipRef.current.textContent = `${fmt(info.ore)} / ${fmt(info.oreMax)} minerals`;
      } else {
        hideTip();
      }
    };

    let loop: GameLoop | null = null;
    let controls: PointerControls | null = null;

    // Pixi init is async. If React unmounts before it resolves, defer teardown
    // until init completes so we never destroy a half-built renderer.
    render
      .mount(mount)
      .then(async () => {
        if (cancelled) {
          render.destroy();
          return;
        }
        ready = true;
        // Load THIS account's cloud save (falls back to the offline cache); none → onboarding.
        try {
          const data = await loadCloudSave(account.id);
          if (cancelled) {
            render.destroy();
            return;
          }
          if (data) state.load(data as Parameters<typeof state.load>[0]);
          else setOnboard(true);
        } catch (e) {
          console.warn("[space-adventura] cloud save load failed:", e);
          setOnboard(true);
        }
        controls = new PointerControls(mount, render, {
          onTapWorld: (x, y) => {
            audio.unlock(); // first gesture satisfies mobile autoplay
            if (aimingRef.current) {
              state.setCannonAim(x, y); // lock the spinal-cannon aim point
              aimingRef.current = false;
              setAiming(false);
              setSnap(state.snapshot());
            } else {
              state.commandTo(x, y); // tap asteroid → send drones; else pilot hull
            }
          },
          onHover: (clientX, clientY) => {
            const rect = mount.getBoundingClientRect();
            const w = render.screenToWorld(clientX - rect.left, clientY - rect.top);
            const id = state.pickBodyAt(w.x, w.y);
            const info = id ? state.bodyOre(id) : null;
            if (id && info) showTip(clientX, clientY, info.ore, info.oreMax);
            else hideTip();
          },
          onHoverEnd: hideTip,
        });
        loop = new GameLoop({
          update: (dt) => state.update(dt),
          render: (alpha) => {
            try {
              render.render(state, alpha);
            } catch (e) {
              console.error("[space-swarm] render error:", e);
              loop?.stop();
            }
          },
        });
        loop.start();
      })
      .catch((err) => console.error("[space-swarm] engine init failed:", err));

    // Feed the HUD ~5×/sec without touching the render path.
    const poll = window.setInterval(() => {
      setSnap(state.snapshot());
      refreshTip(); // keep the hovered asteroid's ore count live
    }, 200);

    return () => {
      cancelled = true;
      save(); // persist final progress on unmount
      window.clearInterval(poll);
      window.clearInterval(saveTimer);
      loop?.stop();
      controls?.destroy();
      if (ready) render.destroy(); // if not yet ready, the .then() above cleans up
    };
  }, [started, account.id]);

  // Live "players online" presence — active whenever logged in (menu + in-game).
  useEffect(() => {
    return joinOnlinePresence(account, setOnline);
  }, [account]);

  // Log this play session (login time + heartbeat'd last-seen) for the admin dashboard.
  useEffect(() => {
    void startSession(account);
    const beat = window.setInterval(() => void heartbeatSession(), 45_000);
    const bump = () => void heartbeatSession(); // best-effort freshen on tab hide / close
    window.addEventListener("beforeunload", bump);
    document.addEventListener("visibilitychange", bump);
    return () => {
      window.clearInterval(beat);
      window.removeEventListener("beforeunload", bump);
      document.removeEventListener("visibilitychange", bump);
      void endSession();
    };
    // key on the primitives, not the account object — auth token refresh (~hourly) hands us a
    // NEW account object with the same id, and we don't want that to split one play session in two.
  }, [account.id, account.username]);

  // Read the saved hull tier to gate the Hub (Mk II+). Refresh whenever we return to the menu.
  useEffect(() => {
    let ok = true;
    void loadCloudSave(account.id).then((d) => {
      const upg = (d as { upgrades?: string[] } | null)?.upgrades;
      if (ok) setHubTier(tierFromUpgrades(Array.isArray(upg) ? upg : []));
    });
    return () => {
      ok = false;
    };
  }, [account.id, started, inHub]);

  const handlePauseChange = useCallback((paused: boolean) => {
    if (stateRef.current) stateRef.current.paused = paused;
  }, []);
  const handleStartGame = useCallback((name: string) => {
    const st = stateRef.current;
    if (st) {
      st.setPlayerName(name);
      st.startTutorial(); // begin the first-run coaching
      void saveCloudSave(account.id, st.serialize());
      setSnap(st.snapshot());
    }
    setOnboard(false);
  }, [account.id]);
  const handleReset = useCallback(() => {
    resettingRef.current = true; // stop the unmount save from re-writing progress
    // Wipe the cloud save + local cache, then reboot into a fresh game → onboarding.
    void deleteCloudSave(account.id).finally(() => window.location.reload());
  }, [account.id]);
  const handleBackToMenu = useCallback(() => {
    const st = stateRef.current;
    if (st) void saveCloudSave(account.id, st.serialize()); // persist before leaving the game
    setStarted(false); // show the main menu; the boot effect cleanup tears the engine down
  }, [account.id]);
  const handleToggleRange = useCallback(() => {
    stateRef.current?.toggleMiningRange();
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);
  const handleToggleMining = useCallback(() => {
    stateRef.current?.toggleHullMining();
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);
  const handleToggleAutoMine = useCallback(() => {
    stateRef.current?.toggleAutoMine();
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);
  const handleToggleDrones = useCallback(() => {
    stateRef.current?.toggleDrones();
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);
  const handleToggleCannons = useCallback(() => {
    stateRef.current?.toggleCannons();
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);
  const handleToggleMechDrones = useCallback(() => {
    stateRef.current?.toggleMechDrones();
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);
  const handleAimCannons = useCallback(() => {
    aimingRef.current = true;
    setAiming(true);
  }, []);
  const handleFireCannons = useCallback(() => {
    stateRef.current?.fireCannons();
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);
  const handleLaunchMissiles = useCallback(() => {
    stateRef.current?.launchMissileSalvo();
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);
  const handleFireWormBeam = useCallback(() => {
    stateRef.current?.fireWormBeam();
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);
  const handleFireWormMissiles = useCallback(() => {
    stateRef.current?.fireWormMissiles();
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);
  const handleArmRift = useCallback(() => {
    stateRef.current?.armRift();
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);
  const handleRiftTarget = useCallback((x: number, y: number) => {
    stateRef.current?.riftTarget(x, y);
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);
  const handleFireCoil = useCallback(() => {
    stateRef.current?.fireCoil();
    if (stateRef.current) setSnap(stateRef.current.snapshot());
  }, []);

  if (inHub) {
    return <HubCanvas account={account} onExit={() => setInHub(false)} />;
  }

  return (
    <div className="game-root">
      <div ref={mountRef} className="canvas-mount" />
      <div ref={tipRef} className="asteroid-tip" />
      <HUD
        snap={snap}
        onPurchaseUpgrade={(id) => {
          stateRef.current?.purchaseUpgrade(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onWarp={() => {
          stateRef.current?.warp();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onExit={() => stateRef.current?.exitToSystem()}
        onPauseChange={handlePauseChange}
        onToggleRange={handleToggleRange}
        onToggleMining={handleToggleMining}
        onToggleAutoMine={handleToggleAutoMine}
        onToggleDrones={handleToggleDrones}
        onToggleCannons={handleToggleCannons}
        onToggleMechDrones={handleToggleMechDrones}
        onAimCannons={handleAimCannons}
        onFireCannons={handleFireCannons}
        onLaunchMissiles={handleLaunchMissiles}
        onFireWormBeam={handleFireWormBeam}
        onFireWormMissiles={handleFireWormMissiles}
        onArmRift={handleArmRift}
        onRiftTarget={handleRiftTarget}
        onWaypoint={(x, y) => stateRef.current?.setMoveTarget(x, y)}
        onFireCoil={handleFireCoil}
        onFireRiftSwarm={() => stateRef.current?.fireRiftSwarm()}
        cannonAiming={aiming}
        onTrade={(n) => {
          stateRef.current?.tradeForUnits(n);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onBuyMarket={(id) => {
          stateRef.current?.buyMarketItem(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onRepair={() => {
          stateRef.current?.repairHull();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onEquipShieldMod={(id) => {
          stateRef.current?.equipShieldMod(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUnequipShieldMod={() => {
          stateRef.current?.unequipShieldMod();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUpgradeShieldMod={(id) => {
          stateRef.current?.upgradeMod(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onBuildMod={() => {
          const mod = stateRef.current?.buildShieldMod() ?? null;
          if (stateRef.current) setSnap(stateRef.current.snapshot());
          return mod;
        }}
        onSellMod={(id) => {
          stateRef.current?.sellMod(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onSellItem={(id) => {
          stateRef.current?.sellItem(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onClearNotice={() => {
          stateRef.current?.clearNotice();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onClearBossResult={() => {
          stateRef.current?.clearBossResult();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onCraft={(id) => {
          stateRef.current?.craft(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onMarkInventorySeen={() => {
          stateRef.current?.markInventorySeen();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onBuyTurret={() => {
          stateRef.current?.buyTurret("turret_basic");
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUnlockSlot={(slot) => {
          stateRef.current?.unlockTurretSlot(slot);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onEquipTurret={(slot) => {
          stateRef.current?.equipTurret(slot, "turret_basic");
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUnequipTurret={(slot) => {
          stateRef.current?.unequipTurret(slot);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onBuyCannon={() => {
          stateRef.current?.buyCannon();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUnlockCannonSlot={(slot) => {
          stateRef.current?.unlockCannonSlot(slot);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onEquipCannon={(slot) => {
          stateRef.current?.equipCannon(slot, "cannon_basic");
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUnequipCannon={(slot) => {
          stateRef.current?.unequipCannon(slot);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUnlockBigCannonSlot={(slot) => {
          stateRef.current?.unlockBigCannonSlot(slot);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onEquipBigCannon={(slot) => {
          stateRef.current?.equipBigCannon(slot, "cannon_big");
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUnequipBigCannon={(slot) => {
          stateRef.current?.unequipBigCannon(slot);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onBuyFighter={(slot) => {
          stateRef.current?.buyFighter(slot);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onDismissFighter={(slot) => {
          stateRef.current?.dismissFighter(slot);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onAssignPilot={(slot, crewId) => {
          stateRef.current?.assignPilot(slot, crewId);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUnassignPilot={(slot) => {
          stateRef.current?.unassignPilot(slot);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onCallFormation={() => {
          stateRef.current?.callFleetFormation();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onRecruit={(type) => {
          stateRef.current?.recruitCrew(type);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUpgradeCrew={(id) => {
          stateRef.current?.upgradeCrew(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onRerollCrew={(id) => {
          stateRef.current?.rerollCrew(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onEquipMdc={(id) => {
          stateRef.current?.equipMdc(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUnequipMdc={() => {
          stateRef.current?.unequipMdc();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onEquipRdc={(id) => {
          stateRef.current?.equipRdc(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUnequipRdc={() => {
          stateRef.current?.unequipRdc();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onEquipGc={(id) => {
          stateRef.current?.equipGc(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUnequipGc={() => {
          stateRef.current?.unequipGc();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onEquipFdc={(id) => {
          stateRef.current?.equipFdc(id);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onUnequipFdc={() => {
          stateRef.current?.unequipFdc();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onRespawn={() => {
          stateRef.current?.respawnPlayer();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onSkipTutorial={() => {
          stateRef.current?.skipTutorial();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onAdvanceDialog={() => {
          stateRef.current?.advanceDialog();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onReset={handleReset}
        onBackToMenu={handleBackToMenu}
        onAdminSetLevel={(n) => {
          stateRef.current?.adminSetLevel(n);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onAdminAddSkill={(n) => {
          stateRef.current?.adminAddSkill(n);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onAdminAddMinerals={(n) => {
          stateRef.current?.adminAddMinerals(n);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onAdminAddUnits={(n) => {
          stateRef.current?.adminAddUnits(n);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onAdminSetWarps={(n) => {
          stateRef.current?.adminSetWarps(n);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onAdminSetMk={(tier, path) => {
          stateRef.current?.adminSetMk(tier, path);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onAdminAddItem={(id, n) => {
          stateRef.current?.adminAddItem(id, n);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onAdminSpawnEater={() => {
          stateRef.current?.adminSpawnEater();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onAdminSpawnStation={() => {
          stateRef.current?.adminSpawnStation();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onAdminUnlockWorm={(mk) => {
          stateRef.current?.adminUnlockWorm(mk);
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
        onAdminGetAllUpgrades={() => {
          stateRef.current?.adminGetAllUpgrades();
          if (stateRef.current) setSnap(stateRef.current.snapshot());
        }}
      />
      {onboard && <Onboarding onStart={handleStartGame} />}
      {!started && (
        <MainMenu
          onPlay={() => setStarted(true)}
          onEnterHub={() => setInHub(true)}
          hubLocked={hubTier < 1}
          username={account.username}
          online={online}
          onLogout={onLogout}
        />
      )}
    </div>
  );
}

function fmt(n: number): string {
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "k";
  return Math.floor(n).toString();
}
